import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import Loader from "../../components/Global/Loader";
import { UserContext } from "../../context/UserContext";

const getStatusStyles = (status) => {
  const baseClasses = "inline-block text-center px-2 py-2 w-24 rounded-full";

  switch (status) {
    case "Pending":
      return `${baseClasses} text-xs py-3 px-4 bg-blue-200 text-blue-800`;
    case "Approved":
      return `${baseClasses} text-xs py-3 px-4 bg-green-200 text-green-800`;
    case "Rejected":
      return `${baseClasses} text-xs py-3 px-4 bg-green-200 text-red-800`;
    case "In Process":
      return `${baseClasses} text-xs py-3 px-4 bg-yellow-200 text-yellow-800`;
    case "Booked":
      return `${baseClasses} text-xs py-3 px-4 bg-purple-200 text-purple-800`;
    default:
      return `${baseClasses} text-xs py-3 px-4 bg-gray-200 text-gray-800`; // fallback style
  }
};



const ConsultationComponent = () => {

  const { setIsEligible } = useContext(UserContext);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isBooked, setIsBooked] = useState(false);
  const [activeTab, setActiveTab] = useState("booked");
  const [hasBookedConsultation, setHasBookedConsultation] = useState(false);
  const [previousConsultations, setPreviousConsultations] = useState([]);
  const [ongoingConsultation, setOngoingConsultation] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [hasReviewed, setHasReviewed] = useState(false);

  const bookNewConsultation = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/consultation/new`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        }
      });
      if (response?.data?.success) {
        setIsBooked(true);
        setActiveTab("booked");
        getConsultations();
      }
    } catch (error) {
      // console.error("Error booking new consultation:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getConsultations = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/consultation/student`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (response?.data?.success) {
        setHasBookedConsultation(response.data.data.hasBookedConsultation);
        setPreviousConsultations(response.data.data.previousConsultations);
        setOngoingConsultation(response.data.data.ongoingConsultation);
        setRemarks(response.data.data.ongoingConsultation?.studentReview || "");

        if (response.data.data.ongoingConsultation !== null) {
          setActiveTab("booked");
        } else {
          setActiveTab("previous");
        }

        if (response.data.data.isEligible) {
          setIsEligible(true);
        } else {
          setIsEligible(false);
        }

      }
    } catch (error) {
      // console.error("Error fetching consultations:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getConsultations();
  }, []);


  const handleRemarksChange = (e) => {
    setRemarks(e.target.value);
  };

  const submitConsultationReview = async (studentReview) => {
    try {
      setIsLoading(true);
      const response = await axios.patch(`${process.env.REACT_APP_BASE_URL}/consultation/${ongoingConsultation._id}/update`, {
        studentReview: studentReview,
        hasReviewedByStudent: true
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        }
      });
      if (response) {
        setIsBooked(false);
        setActiveTab("previous");
        getConsultations();
        setHasReviewed(true);
      }
    } catch (error) {
      // console.error("Error submitting consultation review:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      {isLoading && <Loader />}
      {
        isBooked && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
              {/* Modal Content */}
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Your Consultation is Being Processed
              </h2>
              <h4 className="text-sm text-gray-600 mb-6">
                A consultant will be assigned shortly to schedule your meeting. Please check the system for updates and join the meeting once it's scheduled.
              </h4>
              <div className="flex justify-end space-x-4">
                {/* Proceed Button */}
                <button
                  onClick={() => setIsBooked(false)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        hasReviewed && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
              {/* Modal Content */}
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Submission Successful
              </h2>
              <h4 className="text-sm text-gray-600 mb-6">
                Your feedback has been successfully submitted. We appreciate your input.
              </h4>
              <div className="flex justify-end space-x-4">
                {/* Proceed Button */}
                <button
                  onClick={() => setHasReviewed(false)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        error && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
              {/* Modal Content */}
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Something went wrong!
              </h2>
              <h4 className="text-sm text-gray-600 mb-6">
                We encountered an issue while processing your request. Please try again later. If the problem persists, contact support.
              </h4>
              <div className="flex justify-end space-x-4">
                {/* Proceed Button */}
                <button
                  onClick={() => setError(null)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none"
                >
                  Okay
                </button>
              </div>
            </div>
          </div>
        )
      }
      <div className="relative pl-36 pr-12 mt-16 w-full py-8 bg-[#f4f8fb] h-screen">
        <div className="px-10 py-6 bg-white rounded shadow-md">
          <div className="flex justify-between items-center mb-4">
            <div className="flex space-x-4">
              <button
                className={`px-4 py-2 text-lg font-semibold ${activeTab === "booked" ? " text-blue-500 border-b-2 border-blue-500" : "text-gray-600"}`}
                onClick={() => setActiveTab("booked")}
              >
                Booked Consultation
              </button>
              <button
                className={`px-4 py-2 text-lg font-semibold ${activeTab === "previous" ? " text-blue-500 border-b-2 border-blue-500" : "text-gray-600"}`}
                onClick={() => setActiveTab("previous")}
              >
                Previous Consultations
              </button>
            </div>
            <button
              className={`px-4 py-3 font-semibold text-white rounded ${hasBookedConsultation ? "bg-blue-300 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              disabled={hasBookedConsultation}
              onClick={bookNewConsultation}
            >
              <div className="fa fa-plus mr-4"></div>
              Book New Consultation
            </button>

          </div>

          {activeTab === "booked" ? (
            <div>
              {ongoingConsultation ? (
                <div className="p-4 bg-white rounded">
                  {/* <h3 className="text-lg font-bold text-gray-500 mb-4">Ongoing Consultation</h3> */}
                  <div className="grid grid-cols-2 gap-4">
                    <p><strong>ID:</strong> {ongoingConsultation.consultationId}</p>
                    <p><strong>Consultant:</strong> {ongoingConsultation.consultantName || "TBD"}</p>
                    <p>
                      <strong>Date:</strong> {ongoingConsultation.consultationDate
                        ? new Date(ongoingConsultation.consultationDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                        : "TBD"}
                    </p>

                    <p>
                      <strong>Time:</strong> {ongoingConsultation.consultationTime
                        ? new Date(`1970-01-01T${ongoingConsultation.consultationTime}`).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })
                        : "TBD"}
                    </p>

                    <p>
                      <strong className="mr-1">Status:</strong>
                      <span className={getStatusStyles(ongoingConsultation.status)}>
                        {['Approved', 'Rejected'].includes(ongoingConsultation.status)
                          ? 'Conducted'
                          : ongoingConsultation.status}
                      </span>
                    </p>
                    <p>
                      <strong>Meet Link:</strong>
                      {ongoingConsultation.meetingLink ? (
                        <a
                          href={ongoingConsultation.meetingLink}
                          className="text-blue-500 underline ml-2"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Join Meeting
                        </a>
                      ) : (
                        <span className="text-gray-700 ml-1">Meeting link not available</span>
                      )}
                    </p>
                  </div>

                  {ongoingConsultation.isConducted && (
                    <div className="mt-4">
                      <textarea
                        className="w-full p-2 border border-gray-300 rounded"
                        rows="3"
                        placeholder="Enter your review..."
                        value={remarks}
                        onChange={handleRemarksChange}
                        readOnly={ongoingConsultation.hasReviewedByStudent}
                      />
                      {!ongoingConsultation.hasReviewedByStudent && <button
                        className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        onClick={() => submitConsultationReview(remarks)}

                      >
                        Submit Review
                      </button>}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-center text-gray-600 mt-8">
                  No ongoing consultation found.
                </p>
              )}
            </div>
          ) : (
            <div>
              {/* <h3 className="text-lg font-bold text-gray-500 mb-4">Previous Consultations</h3> */}
              <table className="w-full table-auto border-t border-b border-gray-300">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="px-4 py-2 text-center text-gray-400">Consultation ID</th>
                    <th className="px-4 py-2 text-center text-gray-400">Consultant Name</th>
                    <th className="px-4 py-2 text-center text-gray-400">Date</th>
                    <th className="px-4 py-2 text-center text-gray-400">Time</th>
                    {/* <th className="px-4 py-2 text-center text-gray-400">Status</th> */}
                    <th className="px-4 py-2 text-center text-gray-400">Review</th>
                  </tr>
                </thead>
                <tbody>
                  {previousConsultations.length > 0 ? (
                    previousConsultations.map((reviewedConsultation) => (
                      <tr key={reviewedConsultation._id} className="border-b border-gray-300 text-center">
                        <td className="px-4 py-4 text-gray-700 text-sm">{reviewedConsultation.consultationId}</td>
                        <td className="px-4 py-4 text-gray-700 text-sm">{reviewedConsultation.consultantName}</td>
                        <td className="px-4 py-4 text-gray-700 text-sm">
                          {new Date(reviewedConsultation.consultationDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </td>
                        <td className="px-4 py-4 text-gray-700 text-sm">
                          {reviewedConsultation.consultationTime
                            ? new Date(`1970-01-01T${reviewedConsultation.consultationTime}`).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true,
                            })
                            : "TBD"}
                        </td>
                        {/* <td className="px-4 py-4 text-center">
                          <span className={getStatusStyles(reviewedConsultation.status)}>
                            {reviewedConsultation.status}
                          </span>
                        </td> */}
                        <td className="px-4 py-4 text-gray-700 text-sm text-center">
                          <div className="relative group">
                            <div className="truncate max-w-[150px] mx-auto text-center cursor-pointer">
                              {reviewedConsultation.studentReview || "No Review"}
                            </div>
                            {/* Tooltip for full review */}
                            {reviewedConsultation.studentReview && (
                              <div className="absolute z-10 invisible group-hover:visible bg-white text-black border-2 shadow-lg text-sm px-2 py-1 rounded mt-2 whitespace-normal max-w-[300px] break-words transform -translate-x-1/2 left-1/2">
                                {reviewedConsultation.studentReview}
                              </div>
                            )}
                          </div>
                        </td>

                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-4 py-4 text-gray-400 text-center">
                        No consultations found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ConsultationComponent;
